/**
 * Affiliate referral attribution cookies (affiliate program foundation).
 *
 * tm_ref  — the affiliate code (username) that referred this visitor. 30-day
 *           SLIDING window: every ref-bearing visit rewrites the cookie, so a
 *           second click within the window overwrites the first (last-touch)
 *           and refreshes the expiry.
 * tm_anon — a stable anonymous id used by the backend to dedupe click logging
 *           within 24h. Created on first need, then reused.
 *
 * Cookie attributes: SameSite=Lax (survives cross-site referrals e.g. Discord),
 * Secure on https, path=/, and the parent domain (.trademagic.gg) on prod and
 * staging so api.trademagic.gg receives the cookie on credentialed requests.
 * On localhost no Domain attribute is set (browsers reject .localhost domains);
 * the checkout body param carries the ref there, so attribution still works.
 *
 * The backend /elite/{username} shell route sets these same cookies server-side;
 * this module is the SPA-navigation path and the client-side fallback.
 */

const TM_REF_COOKIE = 'tm_ref';
const TM_ANON_COOKIE = 'tm_anon';
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

const getCookieDomain = (): string | null => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'trademagic.gg' || host.endsWith('.trademagic.gg')) {
    return '.trademagic.gg';
  }
  return null; // localhost / IPs: host-only cookie
};

const setCookie = (name: string, value: string, maxAgeSeconds: number): void => {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    'Path=/',
    'SameSite=Lax',
  ];
  const domain = getCookieDomain();
  if (domain) parts.push(`Domain=${domain}`);
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.substring(name.length + 1)) : null;
};

/** Set / refresh the tm_ref cookie (30-day sliding, last-touch overwrites). */
export const setTmRefCookie = (code: string): void => {
  if (!code) return;
  setCookie(TM_REF_COOKIE, code, THIRTY_DAYS_SECONDS);
};

/** Current tm_ref code, or null when no attribution window is active. */
export const getTmRefCookie = (): string | null => readCookie(TM_REF_COOKIE);

/** Stable anonymous id for click dedupe; created on first call. */
export const getOrCreateAnonymousId = (): string => {
  const existing = readCookie(TM_ANON_COOKIE);
  if (existing) return existing;
  const anonId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  setCookie(TM_ANON_COOKIE, anonId, ONE_YEAR_SECONDS);
  return anonId;
};
